<template>
  <Breadcrumb :items="breadcrumbs" />

  <div class="ds-page">
    <div class="ds-container ds-container--wide">
      <div class="ds-page-header">
        <div>
          <h1 class="ds-page-header__title">تقارير الامتحان</h1>
          <p class="ds-page-header__subtitle">
            {{ exam?.title || 'الامتحان' }}
            <span v-if="exam?.total_degree != null"> — الدرجة الكلية: {{ formatScore(exam.total_degree) }}</span>
          </p>
        </div>
      </div>

      <!-- Summary cards: full accepted-student population (NOT the current page). -->
      <div class="kpi-grid exam-report-kpis mb-4">
        <div class="kpi-card kpi-card--blue">
          <div class="kpi-card__icon"><i class="pi pi-users"></i></div>
          <div class="kpi-card__body">
            <div class="kpi-card__value">{{ stats.accepted_count }}</div>
            <div class="kpi-card__label">إجمالي المقبولين</div>
          </div>
        </div>
        <div class="kpi-card kpi-card--purple">
          <div class="kpi-card__icon"><i class="pi pi-play-circle"></i></div>
          <div class="kpi-card__body">
            <div class="kpi-card__value">{{ stats.started_count }}</div>
            <div class="kpi-card__label">بدأوا الامتحان</div>
          </div>
        </div>
        <div class="kpi-card kpi-card--amber">
          <div class="kpi-card__icon"><i class="pi pi-user-minus"></i></div>
          <div class="kpi-card__body">
            <div class="kpi-card__value">{{ stats.not_started_count }}</div>
            <div class="kpi-card__label">لم يبدأوا</div>
          </div>
        </div>
        <div class="kpi-card kpi-card--green">
          <div class="kpi-card__icon"><i class="pi pi-check-circle"></i></div>
          <div class="kpi-card__body">
            <div class="kpi-card__value">{{ stats.manual_submitted_count }}</div>
            <div class="kpi-card__label">سلّموا بأنفسهم</div>
          </div>
        </div>
        <div class="kpi-card kpi-card--red">
          <div class="kpi-card__icon"><i class="pi pi-clock"></i></div>
          <div class="kpi-card__body">
            <div class="kpi-card__value">{{ stats.timeout_submitted_count }}</div>
            <div class="kpi-card__label">انتهى عليهم الوقت</div>
          </div>
        </div>
      </div>

      <div class="ds-card">
        <div class="ds-card__header">
          <div class="ds-card__header-icon"><i class="pi pi-list"></i></div>
          <p class="ds-card__title">تقارير الطلاب</p>
        </div>

        <!-- Filter bar: search + score range + reset -->
        <div class="row g-2 align-items-end px-3 pb-3">
          <div class="col-lg-3 col-md-6 col-12">
            <div class="form-group">
              <label>بحث عن طالب</label>
              <input type="search" class="form-control" placeholder="اسم الطالب أو الرقم" v-model="word"
                     @keyup="debounceFetch">
            </div>
          </div>
          <div class="col-lg-2 col-md-3 col-6">
            <div class="form-group">
              <label for="min_degree">من درجة</label>
              <input type="number" id="min_degree" class="form-control" placeholder="من" v-model="min_degree"
                     step="0.01" @input="debounceFetchByDegree">
            </div>
          </div>
          <div class="col-lg-2 col-md-3 col-6">
            <div class="form-group">
              <label for="max_degree">إلى درجة</label>
              <input type="number" id="max_degree" class="form-control" placeholder="إلى" v-model="max_degree"
                     step="0.01" @input="debounceFetchByDegree">
            </div>
          </div>
          <div class="col-lg-2 col-md-3 col-6">
            <div class="form-group">
              <label>حالة البدء</label>
              <Dropdown v-model="start_status" :options="startStatusOptions" optionLabel="title"
                        @change="onFilterChange" class="w-100" />
            </div>
          </div>
          <div class="col-lg-2 col-md-3 col-6">
            <div class="form-group">
              <label>طريقة التسليم</label>
              <Dropdown v-model="submission_type" :options="submissionTypeOptions" optionLabel="title"
                        @change="onFilterChange" class="w-100" />
            </div>
          </div>
          <div class="col-lg-1 col-md-3 col-6">
            <div class="form-group">
              <label class="non_opacity">إعادة</label>
              <button type="button" class="btn btn-outline-secondary w-100" @click="resetFilters"
                      title="إعادة ضبط" aria-label="إعادة ضبط">
                <i class="pi pi-refresh"></i>
              </button>
            </div>
          </div>
        </div>

        <!-- Filter bar (row 2): sorting + elapsed completion-time range -->
        <div class="row g-2 align-items-end px-3 pb-3">
          <div class="col-lg-4 col-md-6 col-12">
            <div class="form-group">
              <label>ترتيب حسب</label>
              <Dropdown v-model="sort" :options="sortOptions" optionLabel="title"
                        @change="onFilterChange" class="w-100" />
            </div>
          </div>
          <div class="col-lg-3 col-md-3 col-6">
            <div class="form-group">
              <label for="min_completion">مدة الإنهاء: من دقيقة</label>
              <input type="number" id="min_completion" class="form-control" placeholder="من" min="0" step="1"
                     v-model="min_completion" @input="debounceFetchByCompletion">
            </div>
          </div>
          <div class="col-lg-3 col-md-3 col-6">
            <div class="form-group">
              <label for="max_completion">إلى دقيقة</label>
              <input type="number" id="max_completion" class="form-control" placeholder="إلى" min="0" step="1"
                     v-model="max_completion" @input="debounceFetchByCompletion">
            </div>
          </div>
        </div>

        <!-- Validation: من > إلى -->
        <div v-if="rangeError" class="alert alert-danger mx-3 py-2 px-3">
          <i class="pi pi-exclamation-triangle me-1"></i>
          قيمة "من درجة" يجب ألا تكون أكبر من "إلى درجة".
        </div>
        <div v-if="completionRangeError" class="alert alert-danger mx-3 py-2 px-3">
          <i class="pi pi-exclamation-triangle me-1"></i>
          قيمة "من دقيقة" يجب ألا تكون أكبر من "إلى دقيقة".
        </div>

        <div class="table-responsive">
          <table class="table table-striped align-middle mb-0">
            <thead>
              <tr>
                <th class="ps-3">#</th>
                <th>اسم الطالب</th>
                <th>رقم الطالب</th>
                <th class="text-center">الدرجة</th>
                <th class="text-center">الدرجة الكلية</th>
                <th class="text-center d-none d-lg-table-cell">بدأ</th>
                <th class="text-center d-none d-lg-table-cell">سلّم</th>
                <th class="text-center d-none d-lg-table-cell">مدة الامتحان</th>
                <th class="text-center d-none d-lg-table-cell">الحالة</th>
                <th class="text-center pe-3">التحكم</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="loading">
                <td colspan="10" class="text-center text-muted py-4">
                  <i class="pi pi-spin pi-spinner"></i> جارٍ التحميل...
                </td>
              </tr>
              <tr v-for="(s, index) in students.data" v-else :key="s.id">
                <td class="ps-3">{{ rowNumber(index) }}</td>
                <td>
                  {{ s.name }}
                  <!-- Compact lifecycle grouped for small screens (req #7) -->
                  <div class="d-lg-none exam-lc-mobile">
                    <span class="exam-lc-mobile__title">تفاصيل الامتحان</span>
                    <span><i class="pi pi-play-circle"></i> {{ s.started_at ? formatTimeOnly(s.started_at) : 'لم يبدأ' }}</span>
                    <span><i class="pi pi-flag"></i> {{ s.submitted_at ? formatTimeOnly(s.submitted_at) : '—' }}</span>
                    <span><i class="pi pi-hourglass"></i> {{ formatCompletion(s) }}</span>
                    <span class="status-badge" :class="submitStatus(s).cls">{{ submitStatus(s).label }}</span>
                  </div>
                </td>
                <td>{{ s.id }}</td>
                <td class="text-center">
                  <span v-if="s.taken" class="status-badge status-badge--accepted">
                    {{ formatScore(s.score) }} / {{ formatScore(s.total_degree) }}
                  </span>
                  <span v-else class="status-badge status-badge--rejected">لم يؤدِ الامتحان</span>
                </td>
                <td class="text-center">{{ formatScore(s.total_degree) }}</td>
                <td class="text-center d-none d-lg-table-cell exam-lc-cell">
                  {{ s.started_at ? formatTimeOnly(s.started_at) : 'لم يبدأ' }}
                </td>
                <td class="text-center d-none d-lg-table-cell exam-lc-cell">
                  {{ s.submitted_at ? formatTimeOnly(s.submitted_at) : '—' }}
                </td>
                <td class="text-center d-none d-lg-table-cell exam-lc-cell">
                  {{ formatCompletion(s) }}
                </td>
                <td class="text-center d-none d-lg-table-cell">
                  <span class="status-badge" :class="submitStatus(s).cls">{{ submitStatus(s).label }}</span>
                </td>
                <td class="text-center pe-3">
                  <div class="d-inline-flex gap-1">
                    <button type="button" class="btn btn-sm btn-outline-primary"
                            :disabled="!s.taken" @click="openStudentResult(s)">
                      <i class="pi pi-eye me-1"></i> عرض
                    </button>
                    <button type="button" class="btn btn-sm btn-outline-danger"
                            :disabled="!s.taken || retakingId === s.id"
                            title="إعادة الامتحان" @click="retakeStudentExam(s)">
                      <i class="pi" :class="retakingId === s.id ? 'pi-spin pi-spinner' : 'pi-replay'"></i>
                      إعادة الامتحان
                    </button>
                  </div>
                </td>
              </tr>
              <tr v-if="!loading && students.data.length === 0">
                <td colspan="10" class="text-center text-muted py-4">
                  {{ emptyMessage }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Footer: page-size on the LEFT, pagination on the RIGHT (RTL: first flex
             child sits at the physical right, last child at the physical left). -->
        <div class="report-footer d-flex flex-wrap align-items-center justify-content-between gap-2 mt-3 pb-3 px-3">
          <div class="report-pagination">
            <pagination v-if="students.meta && students.meta.last_page > 1"
                        :data="students" @pagination-change-page="fetchReport" class="pagination mb-0">
              <template v-slot:prev-nav>&lt;</template>
              <template v-slot:next-nav>&gt;</template>
            </pagination>
          </div>
          <div class="report-page-size d-flex align-items-center gap-2">
            <label class="mb-0 small text-muted">عدد الصفوف</label>
            <Dropdown v-model="per_page" :options="perPages" optionLabel="title"
                      @change="onFilterChange" />
          </div>
        </div>
      </div>
    </div>

    <!-- Per-student result review (reuses the student exam-review data) -->
    <Dialog v-model:visible="viewOpen" modal :dismissableMask="true" maximizable
            :header="dialogHeader" class="exam-result-dialog"
            :style="{ width: 'min(920px, 96vw)' }" :contentStyle="{ direction: 'rtl' }">
      <div v-if="viewLoading" class="text-center py-5">
        <i class="pi pi-spin pi-spinner" style="font-size: 2rem;"></i>
      </div>
      <div v-else-if="viewError" class="text-center text-muted py-5">
        <i class="pi pi-info-circle d-block mb-2" style="font-size: 1.6rem;"></i>
        {{ viewError }}
      </div>
      <template v-else-if="viewData">
        <!-- Summary -->
        <div class="esr-summary">
          <div class="esr-summary__head">
            <div>
              <div class="esr-name">{{ viewData.student.name }}</div>
              <div class="esr-sub">رقم الطالب: {{ viewData.student.id }}
                <span v-if="viewData.attempt"> — المحاولة: {{ viewData.attempt }}</span>
              </div>
            </div>
            <div class="esr-score-chip">
              {{ formatScore(viewData.student_score) }} / {{ formatScore(viewData.exam_total_score) }}
              <span class="esr-pct">{{ viewData.percentage }}%</span>
            </div>
          </div>
          <div class="esr-stats">
            <div class="esr-stat esr-stat--green"><span>{{ viewData.correct_answers }}</span> إجابة صحيحة</div>
            <div class="esr-stat esr-stat--red"><span>{{ viewData.wrong_answers }}</span> إجابة خاطئة</div>
            <div class="esr-stat esr-stat--gray"><span>{{ viewData.unanswered }}</span> لم يُجب</div>
            <div class="esr-stat esr-stat--blue"><span>{{ viewData.total_questions }}</span> إجمالي الأسئلة</div>
          </div>

          <!-- تفاصيل الامتحان (lifecycle) -->
          <div class="esr-lifecycle-card">
            <div class="esr-lifecycle-card__title"><i class="pi pi-clock"></i> تفاصيل الامتحان</div>
            <div class="esr-lc-grid">
              <div class="esr-lc-item">
                <span class="esr-lc-label">وقت بدء الامتحان</span>
                <span class="esr-lc-value">{{ viewData.started_at ? formatDateTime(viewData.started_at) : 'لم يبدأ' }}</span>
              </div>
              <div class="esr-lc-item">
                <span class="esr-lc-label">وقت تسليم الامتحان</span>
                <span class="esr-lc-value">{{ viewData.submitted_at ? formatDateTime(viewData.submitted_at) : '—' }}</span>
              </div>
              <div class="esr-lc-item">
                <span class="esr-lc-label">مدة الحل الفعلية</span>
                <span class="esr-lc-value">{{ solveDuration(viewData.started_at, viewData.submitted_at) || '—' }}</span>
              </div>
              <div class="esr-lc-item">
                <span class="esr-lc-label">طريقة إنهاء الامتحان</span>
                <span class="status-badge" :class="submitStatus(viewData).cls">
                  {{ submitStatus(viewData).label }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Questions -->
        <div class="esr-questions">
          <div v-for="(a, i) in viewData.answers" :key="a.question_id"
               class="esr-q" :class="!a.is_answered ? 'esr-q--gray' : (a.student_is_correct ? 'esr-q--correct' : 'esr-q--wrong')">
            <div class="esr-q__head">
              <span class="esr-q__num">{{ i + 1 }}</span>
              <span class="esr-q__title">{{ a.question_title || '—' }}</span>
              <span class="status-badge" :class="answerStatus(a).cls">{{ answerStatus(a).label }}</span>
            </div>

            <div v-if="a.question_image" class="esr-q__image">
              <Image :src="a.question_image" alt="صورة السؤال" preview imageClass="esr-qimg" />
            </div>

            <div class="esr-q__rows">
              <div class="esr-q__row">
                <span class="esr-q__label">إجابة الطالب:</span>
                <span class="esr-q__val">{{ studentAnswerText(a) }}</span>
              </div>
              <div class="esr-q__row">
                <span class="esr-q__label">الإجابة الصحيحة:</span>
                <span class="esr-q__val esr-q__val--correct">{{ a.correct_answer?.title || '—' }}</span>
              </div>
              <div class="esr-q__row">
                <span class="esr-q__label">الدرجة:</span>
                <span class="esr-q__val">{{ formatScore(a.score) }} / {{ formatScore(a.degree) }}</span>
              </div>
            </div>
          </div>
        </div>
      </template>
    </Dialog>
  </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';
import Dropdown from 'primevue/dropdown';
import Dialog from 'primevue/dialog';
import Image from 'primevue/image';
import pagination from 'laravel-vue-pagination';
import Breadcrumb from '@/components/global/breadcrumb.vue';

export default {
  name: 'exam-report',
  components: { Dropdown, Dialog, Image, pagination, Breadcrumb },
  inject: {
    courseCtx: { default: null },
  },
  data() {
    return {
      examId: this.$route.params.id,
      gradeSlug: this.$route.params.gradeSlug ?? null,
      courseId: this.$route.params.courseId ?? null,
      exam: null,
      stats: {
        accepted_students: 0, examined_students: 0, not_examined_students: 0,
        accepted_count: 0, started_count: 0, not_started_count: 0,
        submitted_count: 0, manual_submitted_count: 0, timeout_submitted_count: 0,
      },
      students: { data: [], meta: null },
      word: '',
      min_degree: '',
      max_degree: '',
      // Table-only lifecycle filters (server-side). id maps to the API query value.
      start_status: { title: 'الكل', id: 'all' },
      startStatusOptions: [
        { title: 'الكل', id: 'all' },
        { title: 'بدأ الامتحان', id: 'started' },
        { title: 'لم يبدأ الامتحان', id: 'not_started' },
      ],
      submission_type: { title: 'الكل', id: 'all' },
      submissionTypeOptions: [
        { title: 'الكل', id: 'all' },
        { title: 'سلّم بنفسه', id: 'manual' },
        { title: 'انتهى الوقت', id: 'timeout' },
      ],
      // Server-side ordering. id maps 1:1 to the API `sort` value; each carries its
      // own primary + secondary rule on the backend (see ExamController::report).
      sort: { title: 'الافتراضي', id: 'default' },
      sortOptions: [
        { title: 'الافتراضي', id: 'default' },
        { title: 'الأسرع تسليمًا', id: 'completion_asc' },
        { title: 'الأبطأ تسليمًا', id: 'completion_desc' },
        { title: 'الدرجة: الأعلى أولًا', id: 'grade_desc' },
        { title: 'الدرجة: الأقل أولًا', id: 'grade_asc' },
        { title: 'وقت البدء — الأحدث', id: 'started_desc' },
        { title: 'وقت البدء — الأقدم', id: 'started_asc' },
        { title: 'وقت التسليم — الأحدث', id: 'submitted_desc' },
        { title: 'وقت التسليم — الأقدم', id: 'submitted_asc' },
      ],
      // Elapsed completion-time range (minutes) — filters submitted students only.
      min_completion: '',
      max_completion: '',
      completionTimeout: null,
      per_page: { title: 20, id: 20 },
      perPages: [
        { title: 20, id: 20 },
        { title: 50, id: 50 },
        { title: 100, id: 100 },
        { title: 'الكل', id: 10000 },
      ],
      loading: false,
      searchTimeout: null,
      degreeTimeout: null,
      // "عرض" — per-student result dialog state.
      viewOpen: false,
      viewLoading: false,
      viewError: '',
      viewData: null,
      activeStudent: null,
      retakingId: null, // student id currently being reset (button spinner)
    };
  },
  computed: {
    isCourseScope() { return !!this.courseId; },
    basePath() {
      return this.isCourseScope ? `/courses/${this.courseId}/exams` : `/${this.gradeSlug}/exams`;
    },
    breadcrumbs() {
      if (this.isCourseScope) {
        const base = this.courseCtx?.breadcrumbBase ?? [];
        return [...base, { name: 'الامتحانات', path: this.basePath }, { name: 'تقارير الامتحان', path: '#' }];
      }
      return [
        { name: 'الرئيسية', path: '/' },
        { name: 'الامتحانات', path: this.basePath },
        { name: 'تقارير الامتحان', path: '#' },
      ];
    },
    // Guard against an impossible score range (من > إلى): block the request and
    // surface a message instead of firing an obviously-empty query.
    rangeError() {
      return this.min_degree !== '' && this.max_degree !== '' &&
        parseFloat(this.min_degree) > parseFloat(this.max_degree);
    },
    // Guard against an impossible elapsed-time range (من دقيقة > إلى دقيقة).
    completionRangeError() {
      return this.min_completion !== '' && this.max_completion !== '' &&
        this.min_completion !== null && this.max_completion !== null &&
        parseFloat(this.min_completion) > parseFloat(this.max_completion);
    },
    emptyMessage() {
      if (this.min_degree !== '' || this.max_degree !== '') {
        return 'لا يوجد طلاب ضمن نطاق الدرجات المحدد.';
      }
      return 'لا يوجد طلاب مقبولون لهذا الامتحان.';
    },
    dialogHeader() {
      const name = this.viewData?.student?.name || this.activeStudent?.name || 'نتيجة الطالب';
      return `نتيجة الطالب — ${name}`;
    },
  },
  methods: {
    formatScore(v) {
      if (v === null || v === undefined || v === '') return '—';
      const n = Number(v);
      // Drop a trailing .00 but keep real decimals (e.g. 72.5).
      return Number.isInteger(n) ? String(n) : String(parseFloat(n.toFixed(2)));
    },
    // started_at / submitted_at arrive as Africa/Cairo ISO-8601 (+03:00); show a
    // readable Egypt-local date+time. Renders '—' when the value is null (historical).
    formatDateTime(v) {
      if (!v) return '—';
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) return '—';
      return d.toLocaleString('ar-EG', {
        dateStyle: 'medium', timeStyle: 'short', timeZone: 'Africa/Cairo',
      });
    },
    // Time-only (12-hour, Arabic ص/م) in the Cairo timezone. Input is an
    // Africa/Cairo ISO-8601 instant (+03:00); we read its Cairo wall-clock via
    // Intl (timeZone Africa/Cairo — never the device timezone), then format 12h.
    // e.g. "2026-08-11T20:35:00+03:00" → "08:35 م".
    formatTimeOnly(v) {
      if (!v) return '';
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) return '';
      const hm = d.toLocaleTimeString('en-GB', {
        hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Africa/Cairo',
      }); // "20:35"
      const [hStr, m] = hm.split(':');
      let h = parseInt(hStr, 10);
      const period = h < 12 ? 'ص' : 'م';
      let h12 = h % 12;
      if (h12 === 0) h12 = 12;
      return `${String(h12).padStart(2, '0')}:${m} ${period}`;
    },
    // Submission-type badge — driven ONLY by the server-stored submit_reason.
    //   manual  → سلّم بنفسه  (green)
    //   timeout → انتهى الوقت (orange/warning)
    //   NULL    → —          (neutral)
    // Accepts the full row (falls back to a bare reason string for safety).
    submitStatus(row) {
      const reason = (row && typeof row === 'object') ? row.submit_reason : row;
      if (reason === 'manual') return { label: 'سلّم بنفسه', cls: 'status-badge--accepted' };
      if (reason === 'timeout') return { label: 'انتهى الوقت', cls: 'status-badge--pending' };
      return { label: '—', cls: '' };
    },
    // مدة الحل الفعلية = submitted_at − started_at, computed ONLY from the two
    // server timestamps (difference of absolute instants — no device clock / Date.now()).
    // Returns null unless BOTH exist and are valid.
    solveDuration(started, submitted) {
      if (!started || !submitted) return null;
      const ms = new Date(submitted).getTime() - new Date(started).getTime();
      if (Number.isNaN(ms) || ms < 0) return null;
      const total = Math.floor(ms / 1000);
      const h = Math.floor(total / 3600);
      const m = Math.floor((total % 3600) / 60);
      const s = total % 60;
      const parts = [];
      if (h) parts.push(`${h} ساعة`);
      if (m) parts.push(`${m} دقيقة`);
      if (s || parts.length === 0) parts.push(`${s} ثانية`);
      return parts.join(' و ');
    },
    // "مدة الامتحان" column — elapsed exam time from the backend's authoritative
    // completion_seconds (submitted_at − started_at of the representative attempt, the
    // same value the sort/filter use). '—' when the student didn't submit. Whole
    // minutes with Arabic pluralisation (e.g. 5 → "5 دقائق", 12 → "12 دقيقة").
    formatCompletion(s) {
      const secs = s ? s.completion_seconds : null;
      if (secs === null || secs === undefined) return '—';
      const mins = Math.floor(secs / 60);
      if (mins <= 0) return 'أقل من دقيقة';
      if (mins === 1) return 'دقيقة';
      if (mins === 2) return 'دقيقتان';
      if (mins >= 3 && mins <= 10) return `${mins} دقائق`;
      return `${mins} دقيقة`;
    },
    rowNumber(index) {
      const meta = this.students.meta;
      const from = meta && meta.from ? meta.from : 1;
      return from + index;
    },
    debounceFetch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => this.fetchReport(1), 1000);
    },
    // Separate timer so typing in the degree fields never cancels a pending search
    // debounce (matches the project convention in grades/students.vue).
    debounceFetchByDegree() {
      clearTimeout(this.degreeTimeout);
      this.degreeTimeout = setTimeout(() => this.fetchReport(1), 800);
    },
    // Separate timer for the completion-minutes inputs (same rationale as degree).
    debounceFetchByCompletion() {
      clearTimeout(this.completionTimeout);
      this.completionTimeout = setTimeout(() => this.fetchReport(1), 800);
    },
    onFilterChange() {
      this.fetchReport(1);
    },
    resetFilters() {
      this.word = '';
      this.min_degree = '';
      this.max_degree = '';
      this.start_status = this.startStatusOptions[0];      // الكل
      this.submission_type = this.submissionTypeOptions[0]; // الكل
      this.sort = this.sortOptions[0];                      // الافتراضي
      this.min_completion = '';
      this.max_completion = '';
      this.per_page = this.perPages[0];                     // 20
      this.fetchReport(1);                                  // reloads at page 1
    },
    // Open the reused exam-result review for THIS exact student (row) + this exam.
    // The backend resolves the same best-score attempt shown in the row.
    openStudentResult(student) {
      this.activeStudent = student;
      this.viewData = null;
      this.viewError = '';
      this.viewOpen = true;
      this.viewLoading = true;
      axios.get(`/exams/${this.examId}/students/${student.id}/result`)
        .then(({ data }) => {
          if (data.status) {
            this.viewData = data.data;
          } else {
            this.viewError = data.message || 'لا توجد نتيجة لعرضها.';
          }
        })
        .catch((e) => {
          this.viewError = e.response?.data?.message || 'تعذّر تحميل نتيجة الطالب.';
        })
        .finally(() => { this.viewLoading = false; });
    },
    studentAnswerText(a) {
      if (!a.is_answered) return 'لم يُجب على هذا السؤال';
      return a.answer_title || a.text_answer || '—';
    },
    // Reset THIS student's result/answers for THIS exam so they can retake it. The
    // backend (admin-authed) verifies the relationship and deletes atomically; the
    // frontend is only the trigger.
    retakeStudentExam(student) {
      Swal.fire({
        title: 'هل أنت متأكد من إعادة الامتحان لهذا الطالب؟',
        text: 'سيتم حذف النتيجة الحالية وسيتمكن الطالب من دخول الامتحان مرة أخرى.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#6c757d',
        cancelButtonText: 'إلغاء',
        confirmButtonText: 'إعادة الامتحان',
      }).then((result) => {
        if (!result.isConfirmed) return;
        this.retakingId = student.id;
        axios.post(`/exams/${this.examId}/students/${student.id}/retake`)
          .then(({ data }) => {
            if (data.status) {
              const toast = Swal.mixin({
                toast: true, position: 'top-end',
                showConfirmButton: false, timer: 2500, timerProgressBar: true,
              });
              toast.fire({ icon: 'success', title: data.message || 'تم إعادة الامتحان للطالب بنجاح.' });
              // Refresh the report so the reset student's score updates.
              this.fetchReport(this.students.meta?.current_page || 1);
            } else {
              Swal.fire('', data.message || 'تعذّر إعادة الامتحان.', 'error');
            }
          })
          .catch((error) => {
            Swal.fire('', error.response?.data?.message || 'تعذّر إعادة الامتحان.', 'error');
          })
          .finally(() => { this.retakingId = null; });
      });
    },
    answerStatus(a) {
      if (!a.is_answered) return { label: 'لم يُجب', cls: 'status-badge--pending' };
      return a.student_is_correct
        ? { label: 'صحيحة', cls: 'status-badge--accepted' }
        : { label: 'خاطئة', cls: 'status-badge--rejected' };
    },
    fetchReport(page = 1) {
      if (this.rangeError || this.completionRangeError) return; // do not send an invalid range
      const pageNumber = Number.isInteger(page) ? page : (parseInt(page, 10) || 1);
      const params = {
        page: pageNumber,
        per_page: this.per_page?.id ?? 20,
      };
      if (this.word) params.word = this.word;
      if (this.min_degree !== '') params.min_degree = this.min_degree;
      if (this.max_degree !== '') params.max_degree = this.max_degree;
      // Table-only lifecycle filters — sent server-side (omit when 'all').
      if (this.start_status?.id && this.start_status.id !== 'all') params.start_status = this.start_status.id;
      if (this.submission_type?.id && this.submission_type.id !== 'all') params.submission_type = this.submission_type.id;
      // Server-side ordering (omit for the default order) + elapsed-time range filter.
      if (this.sort?.id && this.sort.id !== 'default') params.sort = this.sort.id;
      if (this.min_completion !== '' && this.min_completion !== null) params.min_completion_minutes = this.min_completion;
      if (this.max_completion !== '' && this.max_completion !== null) params.max_completion_minutes = this.max_completion;

      this.loading = true;
      axios.get(`/exams/${this.examId}/report`, { params })
        .then(({ data }) => {
          this.exam = data.data.exam;
          this.stats = data.data.stats;
          this.students = data.data.students;
        })
        .catch(() => {
          this.students = { data: [], meta: null };
        })
        .finally(() => { this.loading = false; });
    },
  },
  mounted() {
    this.fetchReport();
  },
};
</script>

<style scoped>
.exam-report-kpis {
  grid-template-columns: repeat(3, 1fr);
}
@media (max-width: 991.98px) {
  .exam-report-kpis { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 575.98px) {
  .exam-report-kpis { grid-template-columns: 1fr; }
}
.non_opacity { opacity: 0; }

/* ── Per-student result dialog ── */
.esr-summary {
  background: var(--surface-hover, #f8fafc);
  border: 1px solid var(--surface-border, #e5e7eb);
  border-radius: 14px;
  padding: 1rem 1.1rem;
  margin-bottom: 1rem;
}
.esr-summary__head {
  display: flex; align-items: center; justify-content: space-between;
  gap: 1rem; flex-wrap: wrap; margin-bottom: 0.85rem;
}
.esr-name { font-size: 1.1rem; font-weight: 800; color: #111827; }
.esr-sub { font-size: 0.8rem; color: #6b7280; margin-top: 0.15rem; }

/* ── Table lifecycle columns (compact, lg+) ── */
.exam-lc-cell { font-size: 0.78rem; color: #374151; white-space: nowrap; }

/* ── Compact lifecycle grouped under the name on small screens ── */
.exam-lc-mobile {
  display: flex; flex-wrap: wrap; align-items: center; gap: 0.35rem 0.6rem;
  margin-top: 0.4rem; font-size: 0.72rem; color: #6b7280;
}
.exam-lc-mobile__title { font-weight: 800; color: #4b5563; width: 100%; }
.exam-lc-mobile i { font-size: 0.7rem; margin-inline-end: 0.15rem; }

/* ── Dialog: تفاصيل الامتحان lifecycle card ── */
.esr-lifecycle-card {
  margin-top: 1rem; border: 1px solid var(--surface-border, #e5e7eb);
  border-radius: 12px; padding: 0.85rem 1rem; background: var(--surface-hover, #f8fafc);
}
.esr-lifecycle-card__title { font-size: 0.9rem; font-weight: 800; color: #111827; margin-bottom: 0.6rem; }
.esr-lc-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 0.6rem 1rem; }
.esr-lc-item { display: flex; flex-direction: column; gap: 0.2rem; }
.esr-lc-label { font-size: 0.72rem; color: #6b7280; font-weight: 700; }
.esr-lc-value { font-size: 0.85rem; color: #1f2937; font-weight: 600; }
@media (max-width: 575.98px) {
  .esr-lc-grid { grid-template-columns: 1fr; }
}
.esr-score-chip {
  display: inline-flex; align-items: center; gap: 0.5rem;
  background: rgba(91,95,239,.1); color: #5B5FEF;
  font-weight: 800; font-size: 1.05rem; padding: 0.45rem 0.9rem; border-radius: 12px;
}
.esr-pct { background: #5B5FEF; color: #fff; border-radius: 8px; padding: 0.1rem 0.5rem; font-size: 0.85rem; }
.esr-stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 0.5rem; }
.esr-stat {
  text-align: center; border-radius: 10px; padding: 0.55rem 0.35rem;
  font-size: 0.72rem; font-weight: 700; color: #6b7280; background: #fff;
  border: 1px solid var(--surface-border, #e5e7eb);
}
.esr-stat span { display: block; font-size: 1.15rem; font-weight: 800; }
.esr-stat--green span { color: #16a34a; }
.esr-stat--red span { color: #dc2626; }
.esr-stat--gray span { color: #6b7280; }
.esr-stat--blue span { color: #5B5FEF; }

.esr-questions { display: flex; flex-direction: column; gap: 0.75rem; }
.esr-q {
  border: 1.5px solid var(--surface-border, #e5e7eb);
  border-radius: 12px; padding: 0.9rem 1rem; background: #fff;
}
.esr-q--correct { border-color: rgba(34,197,94,.35); }
.esr-q--wrong { border-color: rgba(239,68,68,.3); }
.esr-q--gray { border-color: rgba(107,114,128,.25); }
.esr-q__head { display: flex; align-items: flex-start; gap: 0.6rem; margin-bottom: 0.5rem; flex-wrap: wrap; }
.esr-q__num {
  flex-shrink: 0; width: 28px; height: 28px; border-radius: 50%;
  background: #eef0ff; color: #5B5FEF; font-weight: 800; font-size: 0.85rem;
  display: flex; align-items: center; justify-content: center;
}
.esr-q__title { flex: 1; min-width: 0; font-weight: 700; color: #111827; line-height: 1.6; }
.esr-q__image { margin: 0.35rem 0 0.7rem; }
.esr-q__image :deep(.esr-qimg) {
  max-width: 100%; max-height: 260px; height: auto; width: auto;
  border-radius: 10px; border: 1px solid #e5e7eb; object-fit: contain; cursor: zoom-in;
}
.esr-q__rows { display: flex; flex-direction: column; gap: 0.3rem; }
.esr-q__row { display: flex; gap: 0.5rem; font-size: 0.88rem; flex-wrap: wrap; }
.esr-q__label { color: #6b7280; font-weight: 700; min-width: 110px; }
.esr-q__val { color: #1f2937; }
.esr-q__val--correct { color: #16a34a; font-weight: 700; }

@media (max-width: 575.98px) {
  .esr-stats { grid-template-columns: repeat(2, 1fr); }
  .esr-q__label { min-width: 90px; }
}
</style>
